import React from 'react';
import { colorValueToCss } from '../fields/ColorPicker/colorUtils';

/**
 * Helper to convert string/boolean to boolean
 */
function toBool(value) {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return value === 'true';
  return !!value;
}

/**
 * Interactive video component with custom play button overlay.
 * Used only in public mode (not in editor iframe).
 *
 * @param {Object} props
 * @param {Object} props.videoProps - Component props (url, posterUrl, controls, etc.)
 * @param {Object} props.dimensions - Pre-computed dimension object from getVideoDimensions
 */
export default function VideoWithPlayButton({ videoProps, dimensions }) {
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [showPlayButton, setShowPlayButton] = React.useState(true);
  const videoRef = React.useRef(null);

  const handlePlayClick = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
        setShowPlayButton(true);
      } else {
        videoRef.current.play();
        setIsPlaying(true);
        setShowPlayButton(false);
      }
    }
  };

  React.useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handlePlay = () => {
      setIsPlaying(true);
      setShowPlayButton(false);
    };

    const handlePause = () => {
      setIsPlaying(false);
      setShowPlayButton(true);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setShowPlayButton(true);
    };

    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('ended', handleEnded);

    return () => {
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('ended', handleEnded);
    };
  }, []);

  return (
    <>
      <video
        ref={videoRef}
        src={videoProps.url}
        poster={videoProps.posterUrl || undefined}
        controls={toBool(videoProps.controls !== undefined ? videoProps.controls : true)}
        autoPlay={toBool(videoProps.autoplay)}
        loop={toBool(videoProps.loop)}
        muted={toBool(videoProps.muted)}
        playsInline={toBool(videoProps.playsinline !== undefined ? videoProps.playsinline : true)}
        preload={videoProps.preload || 'metadata'}
        onClick={(e) => {
          e.stopPropagation();
          handlePlayClick();
        }}
        style={{
          width: dimensions.isAspectRatioMode || dimensions.isNaturalRatio ? '100%' : dimensions.width,
          height: dimensions.isAspectRatioMode ? '100%' : dimensions.height,
          maxWidth: '100%',
          position: dimensions.isAspectRatioMode ? 'absolute' : 'static',
          top: dimensions.isAspectRatioMode ? 0 : undefined,
          left: dimensions.isAspectRatioMode ? 0 : undefined,
          objectFit: videoProps.objectFit || 'contain',
          borderRadius: `${videoProps.borderRadius || 8}px`,
          opacity: (videoProps.opacity || 100) / 100,
          background: colorValueToCss(videoProps.backgroundColor, '#000000'),
          display: 'block',
          cursor: 'pointer'
        }}
      >
        <source src={videoProps.url} type={videoProps.type || 'video/mp4'} />
        Your browser does not support the video tag.
      </video>

      {toBool(videoProps.playButton) && showPlayButton && (
        <button
          onClick={handlePlayClick}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            borderRadius: `${videoProps.borderRadius || 8}px`,
            border: 'none',
            cursor: 'pointer',
            zIndex: 10,
            transition: 'background-color 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.4)'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.3)'}
          aria-label="Play video"
        >
          <div
            style={{
              width: '80px',
              height: '80px',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
              transition: 'transform 0.2s, background-color 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.1)';
              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
            }}
          >
            <svg
              style={{
                width: '40px',
                height: '40px',
                color: '#111827',
                marginLeft: '4px'
              }}
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
        </button>
      )}
    </>
  );
}
